import { Button } from "@/components/ui/button";
import { Check, Play } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import type { ConnectAgentStep } from "./connect-agent-steps";

// Render-only step row for ConnectAgentDialog, split out to keep the dialog
// under the file-size ceiling. Holds no state of its own — every value is a
// prop, so it stays covered by the dialog's integration test.

interface ConnectAgentStepRowProps {
  step: ConnectAgentStep;
  index: number;
  isActive: boolean;
  isCompleted: boolean;
  runDisabled: boolean;
  onRun: () => void;
  onComplete: () => void;
}

/**
 * Linear-minimal step row: flat, neutral, no per-row card or green accents. Only
 * the active step gets a faint surface + reveals its command + run affordance;
 * done steps quiet down with a small check; pending steps dim.
 */
export function ConnectAgentStepRow({
  step,
  index,
  isActive,
  isCompleted,
  runDisabled,
  onRun,
  onComplete,
}: ConnectAgentStepRowProps) {
  return (
    <div
      className={cn(
        "rounded-lg px-3 py-2.5 transition-colors",
        isActive && !isCompleted && "bg-foreground/[0.04]",
        !isActive && !isCompleted && "opacity-55",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium transition-colors",
            isCompleted || isActive
              ? "bg-foreground/10 text-foreground"
              : "bg-foreground/5 text-muted-foreground",
          )}
        >
          {isCompleted ? <Check size={12} weight="Bold" /> : index + 1}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <p
            className={cn(
              "text-sm font-medium leading-tight",
              isCompleted || isActive
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {step.title}
          </p>

          {isActive && !isCompleted && (
            <>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {step.description}
              </p>
              <div className="rounded-md border border-border/50 bg-background px-3 py-2">
                <code className="block truncate font-mono text-xs text-foreground/90 select-all">
                  {step.command}
                </code>
              </div>
              {step.note && (
                <p className="text-[10px] text-muted-foreground/70">
                  {step.note}
                </p>
              )}
              <div className="flex items-center gap-1.5 pt-0.5">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onRun}
                  disabled={runDisabled}
                >
                  <Play size={13} weight="Bold" />
                  {step.runLabel}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onComplete}
                  className="text-muted-foreground"
                >
                  Mark done
                </Button>
                {step.docsUrl && (
                  <a
                    href={step.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Docs
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
